import React, { useMemo } from 'react';
import {
  TipTapMarkHandler,
  TipTapMarkHandlers,
  TipTapNodeHandler,
  TipTapNodeHandlers,
  TipTapRenderHandlers,
} from './renderer';
import EmoteViewer from '../../extensions/extension-emote/viewer';
import DaVideoViewer from '../../extensions/extension-video/Viewer';
import DaDeviationViewer from '../../extensions/extension-deviation/Viewer/Viewer';
import DaDeviationThumbViewer from '../../extensions/extension-deviation-thumb/Viewer/Viewer';
import DaGalleryViewer from '../../extensions/extension-gallery/Viewer/Viewer';
import MentionViewer from '../../extensions/extension-mention/Viewer/Viewer';
import HashtagViewer from '../../extensions/extension-hashtag/Viewer/Viewer';
import GifViewer from '../../extensions/extension-gif/Viewer/Viewer';
import type { ExtensionsConfigOptions } from '../Editor/types';
import { elmoSaysThisMightBeADeviationUrl } from '../../extensions/extension-deviation/helpers';
import { scrollIntoViewInViewportCenter } from '@wix/da-ds/pkg/utils/scrollIntoViewWithHeader';

export function cleanLinkSrc(linkOrSrc: string, allowedDomains: string[] = []) {
  try {
    const url = new URL(linkOrSrc ?? '');
    if (url && (url.username || url.password)) {
      return '';
    }

    if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) {
      return '';
    }

    if (allowedDomains.length > 0) {
      if (!url) {
        return '';
      }
      for (const domain of allowedDomains) {
        if (url.hostname.endsWith(domain)) {
          return linkOrSrc;
        }
      }
      // No matches on allowed domains, drop out
      return '';
    } else {
      return linkOrSrc;
    }
  } catch {
    return '';
  }
}

const Heading = (props: { level: '1' | '2' | '3' | '4'; textAlign; children: React.ReactNode }) => {
  const style = { textAlign: props.textAlign ?? undefined };
  switch (`${props.level}`) {
    case '2':
      return <h2 style={style}>{props.children}</h2>;
    case '3':
      return <h3 style={style}>{props.children}</h3>;
    default:
      return <h4 style={style}>{props.children}</h4>;
  }
};

function makeMark(
  extConfigValue: boolean | undefined,
  handler: TipTapMarkHandler
): TipTapMarkHandler {
  if (extConfigValue) {
    return handler;
  }
  return props => <>{props.children}</>;
}

function makeNode(extConfigValue, handler: TipTapNodeHandler): TipTapNodeHandler {
  if (extConfigValue) {
    return handler;
  }
  return props => <>{props.children}</>;
}

function isInternalLink(url) {
  return (
    url.startsWith('https://www.deviantart.com') ||
    url.startsWith('#') ||
    elmoSaysThisMightBeADeviationUrl(url)
  );
}

export const useHandlers = (extConfig: ExtensionsConfigOptions) => {
  const handlers = useMemo(() => {
    const markHandlers: TipTapMarkHandlers & {
      textStyle: any;
      highlight: any;
    } = {
      link: makeMark(extConfig.link, props => {
        const href: string = props?.node?.attrs?.href ?? '';
        const isHash = href.startsWith('#');

        let handleClick: any = undefined;
        if (isHash) {
          handleClick = e => {
            const el = document.getElementById(href.replace('#', ''));
            if (el) {
              e.preventDefault();
              scrollIntoViewInViewportCenter(el, undefined, undefined, true);
            }
          };
        }

        return (
          <a
            onClick={handleClick}
            href={isHash ? href : cleanLinkSrc(href)}
            target={isInternalLink(href) ? undefined : '_blank'}
            rel="noopener noreferrer nofollow ugc"
          >
            {props.children}
          </a>
        );
      }),
      bold: makeMark(extConfig.bold, props => <strong>{props.children}</strong>),
      italic: makeMark(extConfig.italic, props => <em>{props.children}</em>),
      underline: makeMark(extConfig.underline, props => <u>{props.children}</u>),
      strike: makeMark(extConfig.strikethrough, props => <s>{props.children}</s>),
      code: props => <>{props.children}</>,
      textStyle: makeMark(extConfig.textColor || extConfig.textFont, props => {
        const styles: React.CSSProperties = {};
        if (props.node.attrs?.fontFamily && extConfig.textFont) {
          styles.fontFamily = props.node.attrs.fontFamily;
        }
        if (props.node.attrs?.color && extConfig.textColor) {
          styles.color = props.node.attrs.color;
        }
        return <span style={styles}>{props.children}</span>;
      }),
      highlight: makeMark(extConfig.textHighlight, props => {
        return <mark style={{ backgroundColor: props.node.attrs?.color }}>{props.children}</mark>;
      }),
    };

    const nodeHandlers: TipTapNodeHandlers & {
      mention: any;
      'da-mention': any;
      anchor: any;
      'link-wrapper': any;
      'da-emote': any;
      'da-deviation': any;
      'da-hashtag': any;
      'da-gallery': any;
      'da-gif': any;
      'da-video': any;
      'da-deviation-thumb': any;
      'da-exclusive-avatar': any;
    } = {
      'link-wrapper': props => {
        return <>{props.children}</>;
      },
      doc: props => <>{props.children}</>,
      text: props => <>{props.node.text}</>,
      paragraph: props => {
        const indentCss =
          props.node.attrs?.indentType === 'line'
            ? {
                textIndent: `${(props.node.attrs?.indentation ?? 0) * 24}px`,
              }
            : {
                marginInlineStart: `${(props.node.attrs?.indentation ?? 0) * 24}px`,
              };
        return (
          <p
            id={props.node.attrs?.id}
            style={{
              textAlign: props.node.attrs?.textAlign,
              ...indentCss,
            }}
          >
            {props.children}
          </p>
        );
      },
      heading: makeNode(extConfig.heading, props => {
        const indentCss =
          props.node.attrs?.indentType === 'line'
            ? {
                textIndent: `${(props.node.attrs?.indentation ?? 0) * 24}px`,
              }
            : {
                marginInlineStart: `${(props.node.attrs?.indentation ?? 0) * 24}px`,
              };
        return (
          <Heading level={props.node.attrs?.level} textAlign={(props.node.attrs as any).textAlign}>
            <span
              style={{
                ...indentCss,
              }}
            >
              {props.children}
            </span>
          </Heading>
        );
      }),
      hardBreak: () => (
        <>
          <br />
        </>
      ),
      hardBreakTrailing: () => (
        <>
          <br />
          <br />
        </>
      ),
      emptyParagraph: () => (
        <p className="empty-p">
          <br />
        </p>
      ),
      bulletList: makeNode(extConfig.bulletList, props => <ul>{props.children}</ul>),
      orderedList: makeNode(extConfig.orderedList, props => <ol>{props.children}</ol>),
      listItem: makeNode(extConfig.bulletList || extConfig.orderedList, props => (
        <li>{props.children}</li>
      )),
      codeBlock: props => <>{props.children}</>,
      horizontalRule: makeNode(extConfig.horizontalRuler, () => <hr />),
      blockquote: makeNode(extConfig.blockquote, props => (
        <blockquote>{props.children}</blockquote>
      )),
      mention: makeNode(extConfig.mention, props => (
        <span data-type="mention" className="mention" data-id={props.node.attrs?.id}>
          <a href={`https://www.deviantart.com/${props.node.attrs?.id}`}>{props.node.attrs?.id}</a>
        </span>
      )),
      'da-mention': makeNode(extConfig.mention, MentionViewer),
      'da-gif': makeNode(extConfig.gif, GifViewer),

      anchor: makeNode(extConfig.anchor, props => (
        <a id={props.node.attrs?.id} data-testid="anchor"></a>
      )),
      'da-emote': makeNode(extConfig.emote, EmoteViewer),
      'da-video': makeNode(extConfig.daVideo, DaVideoViewer),
      'da-deviation': makeNode(extConfig.deviation, DaDeviationViewer),
      'da-gallery': makeNode(extConfig.gallery, DaGalleryViewer),
      'da-hashtag': makeNode(extConfig.hashtag, HashtagViewer),
      'da-deviation-thumb': makeNode(extConfig.deviationThumb, DaDeviationThumbViewer),
      'da-exclusive-avatar': () => <></>,
    };

    const handlerConfig: TipTapRenderHandlers = {
      ...markHandlers,
      ...nodeHandlers,
    };
    return handlerConfig;
  }, [
    extConfig.link,
    extConfig.bold,
    extConfig.italic,
    extConfig.underline,
    extConfig.strikethrough,
    extConfig.textColor,
    extConfig.textFont,
    extConfig.textHighlight,
    extConfig.heading,
    extConfig.bulletList,
    extConfig.orderedList,
    extConfig.horizontalRuler,
    extConfig.blockquote,
    extConfig.mention,
    extConfig.gif,
    extConfig.anchor,
    extConfig.emote,
    extConfig.daVideo,
    extConfig.deviation,
    extConfig.gallery,
    extConfig.hashtag,
    extConfig.deviationThumb,
  ]);

  return handlers;
};
